// ReportPieMidweekVsWeekend.jsx

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Container, Typography, Box } from "@mui/material";
import HoverPaper from "../HoverPaper";
import Colours from "../../../helpers/colours";
import EChartsReact from "echarts-for-react";
import { fns } from "../../../helpers/common";
import NoDataRow from "../../NoDataRow";
import InfoIcon from "../../InfoIcon";

const weekEnd = ["Friday", "Saturday", "Sunday"];

const METRICS = {
    "Total Price": "revenueTotal",
    "Booking Total": "bookingsTotal",
    "Room Nights": "roomNightsTotal",
};

const METRICS_TITLE = {
    "Total Price": "Revenue",
    "Booking Total": "Bookings",
    "Room Nights": "Room Nights",
};

const PIE_DATA_ORDER = ["Midweek", "Weekend"];

const PIE_COLORS = {
    Weekend: "#91CC75",
    Midweek: "#5470C6",
};

const SUMMARY_METRICS = {
    "Total Price": {
        Midweek: "midweekRevenue",
        Weekend: "weekendRevenue",
        format: { left: "currency" },
    },
    "Booking Total": {
        Midweek: "midweekBookings",
        Weekend: "weekendBookings",
        format: {},
    },
};

export const REPORT_PIE_MIDWEEK_VS_WEEKEND = "REPORT_PIE_MIDWEEK_VS_WEEKEND";
const ReportPieMidweekVsWeekend = ({ data, metric, description }) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const groupedData = useMemo(() => {
        const entry = Object.values(data || {})[0];
        const result = entry?.result ?? {};
        const summaryMetric = SUMMARY_METRICS[metric];

        // Use the exact same values calculated for the summary table
        if (summaryMetric) {
            return {
                Weekend: Number(result[summaryMetric.Weekend] ?? 0),
                Midweek: Number(result[summaryMetric.Midweek] ?? 0),
            };
        }

        // Fallback for other metrics
        return Object.values(data || {}).reduce(
            (acc, item) => {
                const keyMap = Object.fromEntries(item.keys || []);
                const value = item.result?.[METRICS[metric]] || 0;

                const day = new Date(
                    Date.UTC(
                        Number(keyMap.Year),
                        Number(keyMap.Month) - 1,
                        Number(keyMap.Day),
                    ),
                ).toLocaleDateString("en-US", {
                    weekday: "long",
                    timeZone: "UTC",
                });

                if (weekEnd.includes(day)) {
                    acc.Weekend += value;
                } else {
                    acc.Midweek += value;
                }

                return acc;
            },
            {
                Weekend: 0,
                Midweek: 0,
            },
        );
    }, [data, metric]);

    const total = useMemo(
        () => groupedData.Weekend + groupedData.Midweek,
        [groupedData],
    );

    const seriesData = useMemo(
        () =>
            PIE_DATA_ORDER.map((name) => ({
                name,
                value: groupedData[name] || 0,
                itemStyle: {
                    color: PIE_COLORS[name],
                },
            })).filter((item) => item.value > 0),
        [groupedData],
    );

    const percentages = useMemo(() => {
        return PIE_DATA_ORDER.reduce((acc, name) => {
            const value = groupedData[name] || 0;
            acc[name] = total > 0 ? Math.round((value / total) * 100) : 0;
            return acc;
        }, {});
    }, [groupedData, total]);

    const formatValue = (value) =>
        fns(hotelID, value, SUMMARY_METRICS[metric]?.format ?? {});

    const option = useMemo(
        () => ({
            tooltip: {
                trigger: "item",
                formatter: ({ name, value, percent, marker }) =>
                    [
                        `${marker} ${name}`,
                        `Value: ${formatValue(value)}`,
                        `Percent: ${percent}%`,
                    ].join("<br/>"),
            },
            legend: {
                bottom: 0,
                left: "center",
                icon: "circle",
                data: PIE_DATA_ORDER.filter(
                    (name) => (groupedData[name] || 0) > 0,
                ),
                formatter: (name) => `${name} ${percentages[name]}%`,
            },
            series: [
                {
                    name: `${METRICS_TITLE[metric]} Distribution`,
                    type: "pie",
                    radius: "58%",
                    data: seriesData,
                    clockwise: false,
                    label: {
                        show: true,
                        formatter: ({ percent }) =>
                            `${Number(percent).toFixed(2)}%`,
                    },
                    emphasis: {
                        scale: true,
                    },
                },
            ],
        }),
        [groupedData, metric, percentages, seriesData],
    );

    return (
        <>
            <InfoIcon
                title={`Midweek vs Weekend ${METRICS_TITLE[metric]}`}
                description={description}
            />

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{
                    p: 2,
                    backgroundColor: Colours.notificationCard,
                    position: "relative",
                }}
            >
                {seriesData.length === 0 ? (
                    <Typography sx={{}}>
                        <Typography
                            sx={{
                                py: 6,
                                textAlign: "center",
                                fontSize: "1rem",
                            }}
                        >
                            <NoDataRow
                                text="No data for these filters"
                                justText
                            />
                        </Typography>
                    </Typography>
                ) : (
                    <>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 16,
                                left: 16,
                                zIndex: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ opacity: 0.7, display: "block" }}
                            >
                                Total
                            </Typography>

                            <Typography variant="h6" fontWeight="bold">
                                {formatValue(total)}
                            </Typography>
                        </Box>

                        <EChartsReact
                            style={{ height: 380, width: "100%" }}
                            option={option}
                        />
                    </>
                )}
            </Container>
        </>
    );
};

export default ReportPieMidweekVsWeekend;
